import React, { useState, useMemo } from 'react';
import {
  TableColumn,
  Table,
  Typography,
  Tag,
  Tabs,
  Button,
  Modal,
  Icon,
  Spinner,
} from '@scale/scaleui';
import { OnboardingFlowEventType, PodType } from '@scale/shared-types';
import {
  IOnboardingFlow,
  FlowTerminusDestinationType,
  OnboardingFlowNodeType,
  OnboardingFlowTerminusNode,
  OnboardingStepType,
  OnboardingFlowStepNode,
  ResumeScreenStepConfig,
} from '@scale/shared/onboardingControlPanel';
import { useQuery } from '@tanstack/react-query-v4';
import {
  findOCPWorkflowsWithUserCount,
  useFetchPayTags,
  findWorkerTeamNameById,
  findSignupFlowsForOnboardingFlow,
  getFlowEvents,
  fetchSignupFlow,
} from './api';
import FilterComponent from '../components/FilterComponent';
import SelectTag from '../components/SelectTag';
import { Link, useLocation } from 'react-router-dom';
import { Routes } from 'react-scale/components/GrowthCenter/urls';
import { getJobConfig, getCampaignsForOCPFlow } from 'react-scale/corp/lib/api/growth';
import { HistoryTableModal } from 'react-scale/components/GrowthCenter/components/HistoryTableModal';
import apiFetch from '@scale/frontend-lib/api-fetch';

enum OnboardingFlowDetailCategory {
  DestinationWorkerTeam = 'Destination Worker Teams',
  SourceSignupFlow = 'Source Signup Flows',
  GrantedWorkerSkill = 'Granted Worker Skills',
  GrantedPayTag = 'Granted Pay Tags',
  ResumeScreen = 'Resume Screens',
  EmailCampaign = 'Email Campaigns',
}

interface IOnboardingFlowDetailModalProps {
  flowId: string;
  flowName: string;
  ids: string[];
  type: OnboardingFlowDetailCategory;
}

const OnboardingFlowDetailModal: React.FC<IOnboardingFlowDetailModalProps> = ({
  flowId,
  flowName,
  ids,
  type,
}) => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const uniqueIds = [...new Set(ids)];

  const { data, isLoading, isError } = useQuery(
    [flowId, type],
    async () => {
      if (type === OnboardingFlowDetailCategory.DestinationWorkerTeam) {
        const workerTeams = await Promise.all(
          uniqueIds.map(async id => ({
            id,
            name: await findWorkerTeamNameById(id),
          })),
        );
        return workerTeams;
      } else if (type === OnboardingFlowDetailCategory.SourceSignupFlow) {
        const signupFlowData = await findSignupFlowsForOnboardingFlow(ids[0]);
        return signupFlowData.filter(flow => flow.active);
      } else if (type === OnboardingFlowDetailCategory.ResumeScreen) {
        const resumeScreensData = await Promise.all(
          uniqueIds.map(async id => ({
            id,
            name: (await getJobConfig(id)).name,
          })),
        );
        return resumeScreensData;
      } else if (type === OnboardingFlowDetailCategory.EmailCampaign) {
        const emailCampaigns = await getCampaignsForOCPFlow(flowId);
        return emailCampaigns.map(campaign => ({
          id: campaign._id,
          name: campaign.name,
        }));
      } else if (type === OnboardingFlowDetailCategory.GrantedWorkerSkill) {
        const signupFlowData = await findSignupFlowsForOnboardingFlow(flowId);
        const activeSignupFlowIds = signupFlowData.filter(flow => flow.active).map(flow => flow.id);
        const workerSkills = await apiFetch(null, 'internal/skills', {
          method: 'GET',
        });
        const workerSkillsMap = workerSkills.reduce(
          (acc: { [key: string]: string }, ws: Record<string, any>) => {
            acc[ws.id] = ws.title;
            return acc;
          },
          {},
        );

        const workerSkillsPromises = activeSignupFlowIds.map(async signupFlowId => {
          const signupFlowQuery = await fetchSignupFlow(signupFlowId);
          const workerSkillGrant = signupFlowQuery.signupFlow.workerSkillGrant;
          if (workerSkillGrant && workerSkillGrant.workerSkillId) {
            const display = `${workerSkillsMap[workerSkillGrant.workerSkillId]} | ${
              workerSkillGrant.status
            }`;
            return {
              id: display,
              name: display,
            };
          }
        });
        const workerSkillsResults = (await Promise.all(workerSkillsPromises)).filter(
          (item): item is { id: string; name: string } => item !== undefined,
        );
        return [...uniqueIds.map(id => ({ id, name: id })), ...workerSkillsResults];
      } else if (type === OnboardingFlowDetailCategory.GrantedPayTag) {
        return uniqueIds.map(id => ({ id, name: id }));
      }
    },
    {
      enabled: isOpen,
    },
  );

  const getLink = (id: string, type: OnboardingFlowDetailCategory) => {
    if (type === OnboardingFlowDetailCategory.DestinationWorkerTeam) {
      return `/corp/team/${id}/overview`;
    } else if (type === OnboardingFlowDetailCategory.ResumeScreen) {
      return `/corp/growth-center/resume-screening/${id}`;
    } else if (type === OnboardingFlowDetailCategory.EmailCampaign) {
      return `/corp/growth-center/communication/setup/${id}`;
    }
  };

  const renderedData = data?.map((item: { id?: string; name: string }) =>
    type !== OnboardingFlowDetailCategory.GrantedWorkerSkill &&
    type !== OnboardingFlowDetailCategory.GrantedPayTag &&
    type !== OnboardingFlowDetailCategory.SourceSignupFlow &&
    item.id ? (
      <a key={item.id} href={getLink(item.id, type)} target="_blank" rel="noopener noreferrer">
        <div className="flex items-center mx-1.5">
          {item.name}
          <Icon icon="external-link-alt" size="sm" />
        </div>
      </a>
    ) : (
      <div className="mx-1.5 my-1">{item.name}</div>
    ),
  );

  return ids.length > 0 ||
    type === OnboardingFlowDetailCategory.EmailCampaign ||
    type === OnboardingFlowDetailCategory.GrantedWorkerSkill ? (
    <>
      <Button onClick={() => setIsOpen(true)} variant="secondary" size="sm">
        View
      </Button>
      <Modal open={isOpen} onClose={() => setIsOpen(false)} title={`${flowName} ${type}`}>
        {isError ? (
          'Error fetching data. Please try again later.'
        ) : isLoading ? (
          <Spinner size="sm" />
        ) : renderedData && renderedData.length > 0 ? (
          renderedData
        ) : (
          'None found'
        )}
      </Modal>
    </>
  ) : null;
};

const OnboardingFlowsDataColumns: TableColumn[] = [
  {
    id: 'name',
    header: 'Funnel Name',
    enableSorting: true,
  },
  {
    id: 'podType',
    header: 'Pod',
    enableSorting: true,
  },
  {
    id: 'userCount',
    header: 'User Count',
    enableSorting: true,
  },
  {
    id: 'destinationWorkerTeams',
    header: OnboardingFlowDetailCategory.DestinationWorkerTeam,
    enableSorting: false,
  },
  {
    id: 'sourceSignupFlows',
    header: OnboardingFlowDetailCategory.SourceSignupFlow,
    enableSorting: false,
  },
  {
    id: 'grantedWorkerSkills',
    header: OnboardingFlowDetailCategory.GrantedWorkerSkill,
    enableSorting: false,
  },
  {
    id: 'grantedPayTags',
    header: OnboardingFlowDetailCategory.GrantedPayTag,
    enableSorting: false,
  },
  {
    id: 'resumeScreens',
    header: OnboardingFlowDetailCategory.ResumeScreen,
    enableSorting: false,
  },
  {
    id: 'EmailCampaigns',
    header: OnboardingFlowDetailCategory.EmailCampaign,
    enableSorting: false,
  },
  {
    id: 'isPublished',
    header: 'Status',
    enableSorting: false,
    cell: function StatusCell(props: any) {
      const isPublished = props.getValue();
      if (isPublished) {
        return (
          <div>
            <Tag variant="success">live</Tag>
          </div>
        );
      } else {
        return (
          <div>
            <Tag variant="neutral">draft</Tag>
          </div>
        );
      }
    },
  },
  {
    header: 'Actions',
    id: 'actions',
    enableSorting: false,
  },
];

const tabOptions = [{ id: 'Funnels' }, { id: 'Metrics' }];

export const OnboardingFlowsTable: React.FC = () => {
  const { isLoading, data: rawData } = useQuery(
    ['onboarding-flows'],
    findOCPWorkflowsWithUserCount,
  );
  const payTagsQuery = useFetchPayTags();

  const [podsToFilterFor, setPodsToFilterFor] = React.useState<{ value: string; label: string }[]>(
    [],
  );
  const [data, setData] = React.useState<IOnboardingFlow[]>([]);
  const [currentTab, setCurrentTab] = React.useState<string>(tabOptions[0].id);

  const location = useLocation();

  React.useEffect(() => {
    // @ts-expect-error @types/react@18 $FIXME: state type is unknown
    if (location.state?.podsToFilterFor) {
      // @ts-expect-error @types/react@18 $FIXME: state type is unknown
      setPodsToFilterFor(location.state.podsToFilterFor);
    }
  }, [location.state]);

  React.useEffect(() => {
    if (rawData) {
      let filteredData = rawData;
      if (podsToFilterFor.length !== 0) {
        const podTypes = podsToFilterFor.map(pod => pod.value);
        filteredData = rawData.filter((flow: IOnboardingFlow) =>
          podTypes.includes(flow.podType ? flow.podType.toString() : 'not-set'),
        );
      }
      const sortedData = filteredData.sort((a: IOnboardingFlow, b: IOnboardingFlow) =>
        a.name.replace(/\W/g, '').localeCompare(b.name.replace(/\W/g, '')),
      );
      setData(sortedData);
    } else {
      setData([]);
    }
  }, [rawData, podsToFilterFor]);

  const processedData = useMemo(() => {
    return data.map((row: IOnboardingFlow) => ({
      ...row,
      name: (
        <div className="pt-1.5">
          <Link
            to={{
              pathname: `${Routes.OnboardingControlPanel}/${row._id}`,
              state: { podsToFilterFor },
            }}
          >
            <div>
              <Typography.Paragraph2>{row.name}</Typography.Paragraph2>
            </div>
          </Link>
        </div>
      ),
      destinationWorkerTeams: (
        <OnboardingFlowDetailModal
          flowId={row._id}
          flowName={row.name}
          ids={row.nodes
            .filter(node => node.type === OnboardingFlowNodeType.Terminus)
            .filter(
              node =>
                (node as OnboardingFlowTerminusNode).destinationType ===
                FlowTerminusDestinationType.WorkerTeam,
            )
            .map(node => (node as OnboardingFlowTerminusNode).destinationId)
            .filter((id): id is string => !!id)} // Only strings
          type={OnboardingFlowDetailCategory.DestinationWorkerTeam}
        />
      ),
      sourceSignupFlows: (
        <OnboardingFlowDetailModal
          flowId={row._id}
          flowName={row.name}
          ids={[row._id]}
          type={OnboardingFlowDetailCategory.SourceSignupFlow}
        />
      ),
      grantedWorkerSkills: (
        <OnboardingFlowDetailModal
          flowId={row._id}
          flowName={row.name}
          ids={row.edges.flatMap(edge =>
            edge.workerSkills
              ? edge.workerSkills.map(skill => `${skill.name} | ${skill.category}`)
              : [],
          )}
          type={OnboardingFlowDetailCategory.GrantedWorkerSkill}
        />
      ),
      grantedPayTags: (
        <OnboardingFlowDetailModal
          flowId={row._id}
          flowName={row.name}
          ids={row.edges
            .flatMap(edge => edge.tags)
            .filter((id): id is string =>
              id && payTagsQuery.data ? payTagsQuery.data.includes(id) : false,
            )}
          type={OnboardingFlowDetailCategory.GrantedPayTag}
        />
      ),
      resumeScreens: (
        <OnboardingFlowDetailModal
          flowId={row._id}
          flowName={row.name}
          ids={row.nodes
            .filter(
              node => (node as OnboardingFlowStepNode).stepType === OnboardingStepType.ResumeScreen,
            )
            .map(
              node =>
                ((node as OnboardingFlowStepNode).config as ResumeScreenStepConfig)
                  .resumeScreeningConfigId,
            )
            .filter((id): id is string => !!id)} // Only strings
          type={OnboardingFlowDetailCategory.ResumeScreen}
        />
      ),
      EmailCampaigns: (
        <OnboardingFlowDetailModal
          flowId={row._id}
          flowName={row.name}
          // ids of email campaigns are only queried after the modal is opened
          ids={[]}
          type={OnboardingFlowDetailCategory.EmailCampaign}
        />
      ),
      actions: (
        <div className="flex gap-2">
          <HistoryTableModal
            fetchEvents={async () => {
              const flowEvents = await getFlowEvents(row._id);
              const historyTableEvents = flowEvents.map(event => {
                let changes = {};
                switch (event.data.type) {
                  case OnboardingFlowEventType.UpdateFlow:
                    changes = { Reason: event.data.updateFlowChange };
                    break;
                  case OnboardingFlowEventType.DisableFlow:
                    changes = { Reason: event.data.disableReason };
                    break;
                }
                return {
                  user: event.user ? event.user.email : '',
                  operation: event.data.type,
                  timestamp: event.timestamp,
                  changes,
                };
              });
              return historyTableEvents;
            }}
          />
        </div>
      ),
    }));
  }, [data, payTagsQuery.data]);

  const allPods = Object.values(PodType);
  const podsForMetrics =
    podsToFilterFor.length === 0 ||
    (podsToFilterFor.length === 1 && podsToFilterFor[0].value === PodType.NotSet)
      ? allPods.reduce((acc, podType, i) => {
          return i === allPods.length - 1 ? `${acc}"${podType}"%5D` : `${acc}"${podType}"%2C`;
        }, '%5B')
      : podsToFilterFor.reduce((acc, podFilter, i) => {
          return i === podsToFilterFor.length - 1
            ? `${acc}"${podFilter.value}"%5D`
            : `${acc}"${podFilter.value}"%2C`;
        }, '%5B');

  return (
    <div className="flex flex-col gap-4">
      <div className="px-1">
        <div className="flex flex-row justify-between w-full gap-5 items-center border-b border-b-neutral-200">
          <Tabs onChange={setCurrentTab} tabs={tabOptions} value={currentTab} variant="default" />
          <div>
            <FilterComponent
              filterMessage={
                podsToFilterFor.length === 0
                  ? 'Filter'
                  : podsToFilterFor.map(pod => pod.label).join(', ')
              }
            >
              <SelectTag
                showTagId={false}
                value={podsToFilterFor}
                onChange={setPodsToFilterFor}
                multi={true}
                placeholder={'Filter by pod'}
              />
            </FilterComponent>
          </div>
        </div>
      </div>
      {currentTab === 'Metrics' && (
        <iframe
          src={`https://redash.scale.com/dashboards/4558-ocp-allocation-metrics?p_DATE_RANGE=d_last_7_days&p_POD_TYPE=${podsForMetrics}`}
          width="100%"
          height="1500px"
          frameBorder="0"
        />
      )}
      {currentTab === 'Funnels' && (
        <Table
          columns={OnboardingFlowsDataColumns}
          data={processedData}
          isLoading={isLoading}
          defaultPageSize={10}
        />
      )}
    </div>
  );
};
